import { fabric } from 'fabric'
import type { EventBusListener, UseEventBusReturn } from '@vueuse/core'
import { useEventBus } from '@vueuse/core'
import { ref, shallowRef } from 'vue'
import { clone, isArray } from 'lodash-es'
import Utility from './utility'
import WbFrame from './frame'
import type WbConnector from './connector'
import WbDiscussionIcon from './discussionIcon'
import WbGroup from './group'
import type WbDynamic from './dynamic'
import { AlignGuidelines } from './alignGuidlines'
import { DebugData } from './debugData'
import type WbTextBox from './textBox'
import WbArticleDetails from './articleDetails'
import WbModelDetails from './modelDetails'
import { LinkedObjectsLines } from './linkedObjectsLines'
import WbArticleImage from './articleImage'
import type { TemplateSettingType } from './templates/IWhiteboardTemplate'
import utils, { arrayToStringDictionary } from '@/services/utils'
import { whiteboardConstants } from '@/models/constants'
import appConfig from '@/services/appConfig'
import type { WhiteboardDetailsModel } from '@/api/t1/model/whiteboardModel'
import type MyArticle from '@/models/myArticle'
import type CatalogDetails from '@/models/catalogDetails'
import { useUserStore } from '@/store/userData'

interface IHistoryItem {
  op: 'add' | 'rem' | 'mod' | 'reorder' | 'group' | 'ungroup' | 'assignImage'
  obj: IWbObject | IWbObject[]
  state?: { org: string, cur: string } | { org: string, cur: string }[]
  groupsAllObjects?: IWbObject[]
  indexMap?: Map<string, number>
  customData?: any
}

class Whiteboard {
  boardId: string
  moveToObject?: string
  canvas: fabric.Canvas
  panningOpt = { isDragging: false, lastPosX: 0, lastPosY: 0 }
  articleObjsMap = new Map<string, { article: MyArticle, objectIds: Set<string> }>()
  frames = shallowRef<WbFrame[]>([])
  discussions = shallowRef<WbDiscussionIcon[]>([])
  history = shallowRef<IHistoryItem[]>([])
  historyPointer = ref(0)
  mentionableUsersAndGroups = ref<IMentionItem[]>([])
  whiteboardDetailsCreatorDetails: Record<string, any> = {}
  private eventBus: { [event in CanvasEventName]: UseEventBusReturn<CanvasEventType<event>, any> }
  debugData: DebugData
  _mouseDown: boolean = false
  _linkableObject?: IWbObject
  linkedObjectsLines: LinkedObjectsLines
  leftMouseDown: boolean = false
  panningWhileDragging = { x: 0, y: 0 }
  panningTimer: number | null = null
  lastPointerType: 'mouse' | 'pen' | 'touch' | null = null

  constructor(whiteboardId: number, canvasId: string, width: number, height: number, whiteboardDetails?: WhiteboardDetailsModel, moveToObject?: string) {
    this.boardId = `${appConfig.T1Env}${whiteboardId}`
    this.moveToObject = moveToObject
    // Initialize event bus
    this.eventBus = {
      'pan': useEventBus<ICanvasPanEvent>('pan'),
      'zoom': useEventBus<ICanvasZoomEvent>('zoom'),
      'object-added': useEventBus<IObjectAddedEvent>('object-added'),
      'object-removed': useEventBus<IObjectRemovedEvent>('object-removed'),
      'object-modified': useEventBus<IObjectModifiedEvent>('object-modified'),
      'object-moving': useEventBus<IObjectModifiedEvent>('object-moving'),
      'object-modified-server': useEventBus<IObjectModifiedEvent>('object-modified-server'),
      'object-order': useEventBus<IObjectOrderEvent>('object-order'),
      'update-settings': useEventBus<IUpdateSettingsEvent>('update-settings'),
      'add-dynamic-grid': useEventBus<IAddDynamicGridEvent>('add-dynamic-grid'),
      'canvas-objects-loaded': useEventBus<ICanvasObjectsLoadedEvent>('canvas-objects-loaded'),
    }
    // Initialize canvas
    this.canvas = new fabric.Canvas(canvasId, {
      width,
      height,
      fireRightClick: true,
      fireMiddleClick: true,
      stopContextMenu: true,
      preserveObjectStacking: true,
      skipOffscreen: true,
      selectionFullyContained: true,
    })
    // Register events
    this.handlePanning()
    this.handleOnWheel()
    this.handleBg()
    this.handleObjectEvents()
    this.handleSelection()
    this.setWhiteboardCreatorDetails(whiteboardDetails)
    this.setBoardMentionableList(whiteboardDetails)
    this.handleLinkingObjects()

    this.handleDynamicGridChildrenProps()

    const guideline = new AlignGuidelines({
      canvas: this.canvas,
    })
    guideline.init()

    this.debugData = new DebugData(this.canvas)
    this.debugData.init()

    this.linkedObjectsLines = new LinkedObjectsLines(this.canvas)
    this.linkedObjectsLines.init()
  }

  on<T extends CanvasEventName>(event: T, f: EventBusListener<CanvasEventType<T>>) {
    this.eventBus[event].on(f as EventBusListener<CanvasEventType<CanvasEventName>>)
  }

  off<T extends CanvasEventName>(event: T, f: EventBusListener<CanvasEventType<T>>) {
    this.eventBus[event].off(f as EventBusListener<CanvasEventType<CanvasEventName>>)
  }

  private handleSelection() {
    this.canvas.on('selection:created', (opt) => {
      // Remove objects that want to be exluded from group selection
      const removedObj = removeExcludedObjectsFromSelection(this.canvas, opt.selected)
      if (removedObj === 0) {
        // Lock movement if any object has locked movement
        const isAnyLocked = opt.selected?.some(obj => obj.lockMovementX || obj.lockMovementY)
        if (isAnyLocked) {
          this.canvas.getActiveObject()?.set({ lockMovementX: true, lockMovementY: true })
        }

        // Lock scaling if any object has locked scaling
        const isAnyLockedScaling = opt.selected?.some(obj => obj.lockScalingX || obj.lockScalingY)
        if (isAnyLockedScaling) {
          this.canvas.getActiveObject()?.set({ lockScalingX: true, lockScalingY: true })
        }
      }
    })

    this.canvas.on('selection:updated', (opt) => {
      const removedObj = removeExcludedObjectsFromSelection(this.canvas, opt.selected)
      if (removedObj === 0) {
        // Lock movement if any object has locked movement
        const isAnyLocked = opt.selected?.some(obj => obj.lockMovementX || obj.lockMovementY)
        if (isAnyLocked) {
          this.canvas.getActiveObject()?.set({ lockMovementX: true, lockMovementY: true })
        }

        // Lock scaling if any object has locked scaling
        const isAnyLockedScaling = opt.selected?.some(obj => obj.lockScalingX || obj.lockScalingY)
        if (isAnyLockedScaling) {
          this.canvas.getActiveObject()?.set({ lockScalingX: true, lockScalingY: true })
        }
      }
    })

    function removeExcludedObjectsFromSelection(canvas: fabric.Canvas, selectedObjects?: fabric.Object[]) {
      if (selectedObjects && selectedObjects.length > 1) {
        const newSelection = selectedObjects.filter(obj => !((obj as any).excludeFromGroupSelection === true))
        if (newSelection.length === selectedObjects.length) { return 0 }

        canvas.discardActiveObject()
        if (newSelection.length > 1) {
          canvas.setActiveObject(new fabric.ActiveSelection(newSelection, { canvas }))
        }
        else if (newSelection.length === 1) {
          canvas.setActiveObject(newSelection[0])
        }
        else {
          canvas.discardActiveObject()
        }
        canvas.requestRenderAll()
        return selectedObjects.length - newSelection.length
      }
      return 0
    }
  }

  // Handle the ability to move around the board
  private handlePanning() {
    this.canvas.on('mouse:down', (opt) => {
      const evt = opt.e

      if (evt.altKey === true || evt.button === whiteboardConstants.mouseButtons.middle || evt.button === whiteboardConstants.mouseButtons.right) {
        this.panningOpt.isDragging = true
        this.canvas.selection = false
        this.panningOpt.lastPosX = evt.clientX
        this.panningOpt.lastPosY = evt.clientY
      }
      else if (evt.button === whiteboardConstants.mouseButtons.left) {
        this.leftMouseDown = true
      }
    })

    this.canvas.on('mouse:move', (opt) => {
      if (this.panningOpt.isDragging && this.canvas.viewportTransform) {
        this.canvas.setCursor('grabbing')
        const e = opt.e

        const vpt = this.canvas.viewportTransform
        vpt[4] += e.clientX - this.panningOpt.lastPosX
        vpt[5] += e.clientY - this.panningOpt.lastPosY

        this.canvas.requestRenderAll()
        this.panningOpt.lastPosX = e.clientX
        this.panningOpt.lastPosY = e.clientY
        this.eventBus.pan.emit({ event: 'pan', viewport: vpt })
      }
      if (this.leftMouseDown && this.canvas.height && this.canvas.width) {
        let isPanning = false
        if (opt.e.clientY <= 0) {
          this.panningWhileDragging.y = 10
          isPanning = true
        }
        else if (opt.e.clientY >= this.canvas.height - 10) {
          this.panningWhileDragging.y = -10
          isPanning = true
        }
        if (opt.e.clientX <= 90) {
          this.panningWhileDragging.x = 10
          isPanning = true
        }
        else if (opt.e.clientX >= this.canvas.width + 50) {
          this.panningWhileDragging.x = -10
          isPanning = true
        }

        if (isPanning && !this.panningTimer) {
          this.doPanWhileDragging()
          this.panningTimer = setInterval(() => this.doPanWhileDragging(), 50)
        }
        else if (this.panningTimer) {
          this.clearPanningTimer()
        }
      }
    })

    this.canvas.on('mouse:up', () => {
      // on mouse up we want to recalculate new interaction
      // for all objects, so we call setViewportTransform
      if (this.canvas.viewportTransform) {
        this.canvas.setViewportTransform(this.canvas.viewportTransform)
      }
      this.panningOpt.isDragging = false
      this.canvas.selection = true
      this.leftMouseDown = false
      this.clearPanningTimer()
    })
  }

  private doPanWhileDragging() {
    if (this.canvas.viewportTransform) {
      const vpt = this.canvas.viewportTransform
      vpt![4] += this.panningWhileDragging.x
      vpt![5] += this.panningWhileDragging.y
      this.canvas.requestRenderAll()
      this.eventBus.pan.emit({ event: 'pan', viewport: vpt })
    }
  }

  private handleOnWheel() {
    // handel zoom and pan by track pad
    this.canvas.on('mouse:wheel', (opt) => {
      if (opt.e.ctrlKey || opt.e.metaKey) {
        this.handleZooming(opt.e)
      }
      else {
        // pan using track pad
        this.handlePanningByTrackPad(opt.e)
      }
    })
  }

  private clearPanningTimer() {
    if (this.panningTimer) {
      clearInterval(this.panningTimer)
      this.panningTimer = null
      this.panningWhileDragging.x = 0
      this.panningWhileDragging.y = 0
    }
  }

  private handlePanningByTrackPad(event: WheelEvent) {
    event.preventDefault()
    event.stopPropagation()
    if (this.canvas.viewportTransform) {
      const vpt = this.canvas.viewportTransform
      vpt![4] -= event.deltaX
      vpt![5] -= event.deltaY
      this.canvas.requestRenderAll()
      this.eventBus.pan.emit({ event: 'pan', viewport: vpt })
    }
  }

  // Handle the ability to zoom in and out of the board
  private handleZooming(event: WheelEvent) {
    event.preventDefault()
    event.stopPropagation()
    const userStore = useUserStore()

    let delta = event.deltaY
    // console.log(delta, event.wheelDeltaY)
    if (delta < 0 && delta > -40) {
      delta = -40
    }
    else if (delta > 0 && delta < 40) {
      delta = 40
    }
    let zoom = this.canvas.getZoom()
    const oldZoom = zoom
    const factor = userStore.settingsContainer?.whiteboard?.zoomSensitivity as number ?? 90
    zoom *= (0.99 + factor * 0.0001) ** delta
    if (zoom > 5) { zoom = 5 }
    if (zoom < 0.02) { zoom = 0.02 }
    if (zoom !== oldZoom) {
      this.zoom(zoom, { x: event.offsetX, y: event.offsetY }, false)
    }
  }

  private handleBg() {
    this.canvas.setBackgroundColor(new fabric.Pattern({ source: '/images/whiteboard-bg.svg', repeat: 'repeat' }), () => {
      setTimeout(() => this.canvas.requestRenderAll(), 500)
    })
  }

  private handleObjectEvents() {
    this.canvas.on('object:added', (opt) => {
      if (opt.target) {
        if (opt.target instanceof WbFrame) {
          this.loadFrames()
        }
        else if (opt.target instanceof WbDiscussionIcon) {
          this.loadDiscussions()
        }
        this.eventBus['object-added'].emit({ target: opt.target })
      }
      this.reorderObjects()
    })

    this.canvas.on('object:modified', (opt) => {
      if (opt.target) {
        const wbObject = opt.target as IWbObject
        // eslint-disable-next-line ts/ban-ts-comment
        // @ts-expect-error
        if (!opt.ignoreHistory) {
          if (opt.target.isType('activeSelection')) {
            const objs = this.canvas.getActiveObjects() as Array<IWbObject>
            objs.forEach((obj) => {
              if (obj.id) {
                // when moving multiple selected objects together, if frame is included we are setting the child objects visibility false when moving
                // this event is triggered before the frame mouse:up event, so the objects visiblity is still false.
                // hence setting them back to true when adding to history
                if (utils.isDefined(obj.visible) && !obj.visible) {
                  obj.visible = true
                }
                // add the linked objects of current object to history
                const linkedObjects = this.canvas.getObjects().filter((canvasObj: any) => (canvasObj.link && canvasObj.link.id === obj.id && canvasObj.visible))
                linkedObjects.forEach((linkObj: any) => {
                  const existsInList = objs.some(o => o.id === linkObj.id)
                  if (!existsInList) {
                    objs.push(linkObj)
                  }
                })
              }
            })
            this.canvas.discardActiveObject()
            this.addToHistory('mod', objs)
            this.canvas.setActiveObject(new fabric.ActiveSelection(objs, { canvas: this.canvas }))
            this.canvas.requestRenderAll()
          }
          else if (!wbObject.localOnly && wbObject.id) {
            if (wbObject.type === whiteboardConstants.objectTypes.frame) {
              const wbFrame = wbObject as WbFrame
              if (wbFrame.movingChangedObjsMap.size > 0) {
                const allObjs = this.canvas.getObjects() as Array<IWbObject>
                const wbObjects: IWbObject[] = [wbObject]
                allObjs.forEach((obj) => {
                  if (obj.id && wbFrame.isChild(obj.id)) {
                    obj.visible = true
                  }
                  if (obj.id && wbFrame.movingChangedObjsMap.has(obj.id)) {
                    wbObjects.push(obj)
                  }
                  // add the linked objects of current object to history
                  const linkedObjects = this.canvas.getObjects().filter((canvasObj: any) => (canvasObj.link && canvasObj.link.id === obj.id && canvasObj.visible))
                  linkedObjects.forEach((linkObj: any) => {
                    wbObjects.push(linkObj)
                  })
                })
                this.addToHistory('mod', wbObjects)
              }
              else {
                this.addToHistory('mod', wbObject)
              }
            }
            else if (opt.target instanceof WbDiscussionIcon) {
              this.loadDiscussions()
              this.addToHistory('mod', wbObject)
            }
            else {
              const wbObjects: IWbObject[] = [wbObject]
              // add the linked objects of current object to history
              const linkedObjects = this.canvas.getObjects().filter((obj: any) => (obj.link && obj.link.id === wbObject.id && obj.visible))
              linkedObjects.forEach((obj: any) => {
                wbObjects.push(obj)
              })
              if (linkedObjects && linkedObjects.length) {
                this.addToHistory('mod', wbObjects)
              }
              else {
                this.addToHistory('mod', wbObject)
              }
            }
          }
        }
        else if (opt.target instanceof WbDiscussionIcon) {
          this.loadDiscussions()
        }
        this.eventBus['object-modified'].emit({ target: opt.target })
      }
    })

    this.canvas.on('object:removed', (opt) => {
      if (opt.target) {
        if (opt.target instanceof WbFrame) {
          this.loadFrames()
        }
        else if (opt.target instanceof WbDiscussionIcon) {
          this.loadDiscussions()
        }
        this.eventBus['object-removed'].emit({ target: opt.target })
      }
    })

    this.canvas.on('object:moving', (opt) => {
      if (opt.target) {
        this.eventBus['object-moving'].emit({ target: opt.target })
        // as discussed with Andre trigger object:moving event for each object in group/selection, so that the connectors can directly listen to each object movement inside selection
        const objectsKey = '_objects'
        if (opt.target.hasOwnProperty(objectsKey)) { // if object is a group/selection
          opt.target[objectsKey].forEach((object) => {
            object.fire('moving', { e: opt.e, target: object, pointer: opt.pointer, transform: opt.transform, _selection: opt.target })
          })
        }
      }
    })

    this.canvas.on('mouse:up', (opt) => {
      const objectsKey = '_objects'
      if (opt.target?.hasOwnProperty(objectsKey)) { // if object is a group/selection
        opt.target[objectsKey].forEach((object) => {
          object.fire('mouseup', { e: opt.e, target: object, pointer: opt.pointer, transform: opt.transform, _selection: opt.target })
        })
      }
    })
  }

  private loadFrames() {
    this.frames.value = []
    const frames = this.canvas.getObjects(whiteboardConstants.objectTypes.frame) as Array<WbFrame>
    if (frames?.length > 0) {
      frames.sort((a, b) => a.sortOrder - b.sortOrder)
        .forEach(frame => this.frames.value.push(frame))
    }
  }

  emitObjectModifiedServer(obj) {
    if (obj instanceof WbDiscussionIcon) {
      this.loadDiscussions()
    }
    this.eventBus['object-modified-server'].emit({ target: obj })
  }

  private setWhiteboardCreatorDetails(whiteboardDetails: WhiteboardDetailsModel | undefined) {
    if (whiteboardDetails) {
      this.whiteboardDetailsCreatorDetails.createdBy = whiteboardDetails.CreatedBy
      this.whiteboardDetailsCreatorDetails.createdByName = whiteboardDetails.CreatedByName
      this.whiteboardDetailsCreatorDetails.createdByUserName = whiteboardDetails.CreatedByUserName
    }
  }

  private loadDiscussions() {
    this.discussions.value = []
    const discussions = this.canvas.getObjects(whiteboardConstants.objectTypes.discussion) as Array<WbDiscussionIcon>
    if (discussions.length > 0) {
      discussions.forEach((object) => {
        if (object.comments.length) {
          this.discussions.value.push(object)
        }
      })
      this.discussions.value.sort((a, b) => {
        return new Date(b.comments[0].date).getTime() - new Date(a.comments[0].date).getTime()
      })
    }
  }

  getArticleFromMap(catalogCode: number, articleId: number) {
    const mapKey = `${catalogCode}-${articleId}`
    return this.articleObjsMap.get(mapKey)?.article
  }

  addObjToArticleMap(obj: any) {
    if (obj.article && obj.catalogCode && obj.articleId) {
      const mapKey = `${obj.catalogCode}-${obj.articleId}`
      if (!this.articleObjsMap.has(mapKey)) {
        this.articleObjsMap.set(mapKey, { article: obj.article, objectIds: new Set<string>() })
      }
      this.articleObjsMap.get(mapKey)!.objectIds.add(obj.id)
    }
  }

  removeObjectFromArticleMap(obj: any) {
    if (obj.catalogCode && obj.articleId && obj.id) {
      const mapKey = `${obj.catalogCode}-${obj.articleId}`
      if (this.articleObjsMap.has(mapKey)) {
        this.articleObjsMap.get(mapKey)!.objectIds.delete(obj.id)
        if (this.articleObjsMap.get(mapKey)!.objectIds.size === 0) {
          this.articleObjsMap.delete(mapKey)
        }
      }
    }
  }

  updateCanvasArticles(catalog: CatalogDetails, articles: MyArticle[]) {
    const allObjects = this.canvas.getObjects() as Array<IWbObject>
    articles.forEach((article) => {
      const mapKey = `${article.CatalogCode}-${article.Id}`
      if (this.articleObjsMap.has(mapKey)) {
        const mapItem = this.articleObjsMap.get(mapKey)!
        mapItem.article = article
        allObjects.forEach(async (obj) => {
          if (obj.id && mapItem.objectIds.has(obj.id)) {
            if (obj.type === whiteboardConstants.objectTypes.articleImage && obj instanceof WbArticleImage) {
              obj.article = article
              obj.loadTextValue(article)
              obj.articleNumber = article.ArticleNumber
              if (obj.blockImage !== article.BlockImage) {
                obj.imgLoaded = false
                obj.imgLoading = false
              }
              obj.blockImage = article.BlockImage
              obj.status = article.Status === 1
              obj.colorCode = ''
              if (catalog.Config.WhiteboardColorCodingAttribute && catalog.Config.WhiteboardColorCodingAttribute?.length !== 0 && catalog.Config.WhiteboardColorCodingAttribute[0].attribute) {
                for (const configValue of catalog.Config.WhiteboardColorCodingAttribute) {
                  if (utils.isValidStringValue(article[configValue.attribute]) && article[configValue.attribute]?.toString().trim().toLowerCase() === configValue.value.toString().toLowerCase()) {
                    obj.colorCode = configValue.color
                    break
                  }
                }
              }
            }
            else if (obj.type === whiteboardConstants.objectTypes.articleDetails && obj instanceof WbArticleDetails) {
              obj.article = article
              obj.set('text', await WbArticleDetails.getText(article, obj.showLabels, obj.attributes, obj.attributesSeparator, obj.attributePlaceHolder))
            }
            else if (obj.type === whiteboardConstants.objectTypes.modelImage && obj instanceof WbModelDetails) {
              obj.article = article
            }
            else if (obj.type === whiteboardConstants.objectTypes.modelDetails && obj instanceof WbModelDetails) {
              obj.article = article
              obj.set('text', await WbModelDetails.getText(article, obj.showLabels, obj.attributes, obj.attributesSeparator, obj.attributePlaceHolder))
            }
            obj.dirty = true
            this.canvas?.fire('object:modified', { target: obj, ignoreHistory: true })
          }
        })
      }
    })
    if (articles.length) {
      this.canvas.requestRenderAll()
    }
  }

  updateBoardMentionableList(items) {
    this.mentionableUsersAndGroups.value = []
    items.forEach((item) => {
      this.mentionableUsersAndGroups.value.push({
        type: item.type,
        value: item.id,
        label: item.name,
        subTitle: item.subTitle,
      })
    })
    this.mentionableUsersAndGroups.value.push({
      value: this.whiteboardDetailsCreatorDetails.createdBy,
      type: 'user',
      label: this.whiteboardDetailsCreatorDetails.createdByName,
      subTitle: this.whiteboardDetailsCreatorDetails.createdByUserName,
    })
  }

  setBoardMentionableList(whiteboardDetails: WhiteboardDetailsModel | undefined) {
    if (whiteboardDetails) {
      whiteboardDetails.SharedUsersGroups.forEach((userGroup) => {
        this.mentionableUsersAndGroups.value.push({
          value: userGroup.GroupId,
          type: 'group',
          label: userGroup.Name,
          subTitle: userGroup.AccountName,
        })
      })
      whiteboardDetails.SharedUsers.forEach((user) => {
        this.mentionableUsersAndGroups.value.push({
          value: user.UserId,
          type: 'user',
          label: `${user.FirstName} ${user.LastName}`,
          subTitle: user.UserName,
        })
      })
      this.mentionableUsersAndGroups.value.push({
        value: whiteboardDetails.CreatedBy,
        type: 'user',
        label: whiteboardDetails.CreatedByName,
        subTitle: whiteboardDetails.CreatedByUserName,
      })
    }
  }

  /**
   * Resizes the canvas
   *
   * @param width Width of canvas
   * @param height Height of canvas
   */
  setSize(width: number, height: number) {
    this.canvas.setWidth(width)
    this.canvas.setHeight(height)
    this.canvas.calcOffset()
  }

  zoom(factor: number, point?: IPoint, animate = false) {
    if (point) {
      if (!animate) {
        this.canvas.zoomToPoint(point, factor);
        (this.canvas.backgroundColor as fabric.Pattern).patternTransform = [1 / factor, 0, 0, 1 / factor, 0, 0]
      }
      else {
        fabric.util.animate({
          startValue: this.canvas.getZoom(),
          endValue: factor,
          duration: 300,
          onChange: (zoomValue) => {
            this.canvas.zoomToPoint(point, zoomValue)
            this.canvas.requestRenderAll()
          },
          onComplete: () => {
            (this.canvas.backgroundColor as fabric.Pattern).patternTransform = [1 / factor, 0, 0, 1 / factor, 0, 0]
            this.canvas.requestRenderAll()
          },
        })
      }
    }
    else {
      this.canvas.setZoom(factor)
    }
    this.canvas.getObjects().forEach(obj => obj.fire('zoom', { factor, point }))
    this.eventBus.zoom.emit({ event: 'zoom', factor, point })
  }

  reorderObjects() {
    // Bring all discussion objects to front
    this.canvas.getObjects(whiteboardConstants.objectTypes.discussion).forEach((obj) => {
      obj.bringToFront()
    })

    // Send all dynamic grid objects to back
    this.canvas.getObjects(whiteboardConstants.objectTypes.dynamic).forEach((obj) => {
      obj.sendToBack()
    })

    this.canvas.requestRenderAll()
  }

  /**
   * @description select all objects on canvas
   */
  selectAllObjects() {
    this.canvas.discardActiveObject()
    const objs = this.canvas.getObjects() as Array<IWbObject>
    const selectableObj = objs.filter(obj => obj.selectable && !obj.lock && obj.visible)
    this.canvas.setActiveObject(new fabric.ActiveSelection(selectableObj, { canvas: this.canvas }))
    this.canvas.requestRenderAll()
  }

  updateTextTypeObjectStyle(type: 'bold' | 'italic' | 'underline') {
    const allActiveObjects = this.canvas.getActiveObjects()
    const textAndDetailsObject: fabric.Object[] = allActiveObjects.filter(obj => obj.type === whiteboardConstants.objectTypes.articleDetails || obj.type === whiteboardConstants.objectTypes.modelDetails || obj.type === whiteboardConstants.objectTypes.textBox)
    if (textAndDetailsObject.length) {
      const objectToRefer = clone(textAndDetailsObject[0]) as WbTextBox | WbArticleDetails | WbModelDetails
      textAndDetailsObject.forEach((object) => {
        let value = {}
        if (type === 'bold') {
          value = { bold: !(objectToRefer.fontWeight === 'bold') }
        }
        else if (type === 'italic') {
          value = { italic: !(objectToRefer.fontStyle === 'italic') }
        }
        else {
          value = { underline: !(objectToRefer.underline) }
        }
        (object as any).setProp(type, value)
      })
    }
  }

  toogleDebugData() {
    this.debugData.enable(!this.debugData.enabled)
  }

  /**
   * @description move selected objects from canvas on keyboard up, down, left and right
   */
  moveObject(event: KeyboardEvent, type: 'up' | 'left' | 'right' | 'down' = 'up') {
    const ao = this.canvas.getActiveObject()
    const movementValue = event.ctrlKey || event.metaKey ? 1 : 5
    if (ao) {
      let isAnyActiveObjectLocked = false
      const objectsKey = '_objects'
      if (ao.hasOwnProperty(objectsKey)) {
        const lockedActiveObjects = ao[objectsKey].filter(a => a.lock)
        isAnyActiveObjectLocked = !!lockedActiveObjects.length
      }
      else {
        const lockKey = 'lock'
        isAnyActiveObjectLocked = !!ao[lockKey]
      }
      if (!isAnyActiveObjectLocked) {
        if ((type === 'up' || type === 'down') && ao.top) {
          if (type === 'up') {
            ao.top -= movementValue
          }
          else {
            ao.top += movementValue
          }
        }
        else if ((type === 'left' || type === 'right') && ao.left) {
          if (type === 'left') {
            ao.left -= movementValue
          }
          else {
            ao.left += movementValue
          }
        }
        ao.setCoords()
        this.canvas.requestRenderAll()
        if (ao.type === whiteboardConstants.objectTypes.dynamic) {
          (ao as WbDynamic).recalcObjectsPosition()
        }
        const e = { x: ao.aCoords!.br.x! + ao.width! + 40, y: ao.aCoords!.tl.y! }
        this.canvas.fire('object:moving', { target: ao, e })
        this.canvas.fire('object:modified', { target: ao })
      }
    }
  }

  /**
   * @description copy the selected object on canvas into whiteboard's clipboard and will clear the system clipboard
   */
  copySelectedObjects() {
    const selectedObjects = this.canvas.getActiveObjects()
    if (selectedObjects.length > 0) {
      const copiedObjects: { object: any, relativePosition: { left: number, top: number } }[] = []
      const referenceObject = selectedObjects[0]
      const txt: string[] = []
      selectedObjects.forEach((obj) => {
        if ((obj as any).localOnly) {
          return
        }
        copiedObjects.push({ object: obj.toObject(), relativePosition: { left: (obj.left || 0) - (referenceObject.left || 0), top: (obj.top || 0) - (referenceObject.top || 0) } })
        if ((obj as any).text) {
          txt.push((obj as any).text as string)
        }
      })
      let htmlText = `<span data-meta="(t1-data-v1)${encodeURI(JSON.stringify(copiedObjects))}(/t1-data-v1)"></span>`
      txt.forEach((t) => {
        htmlText += `<div>${t}</div>\n`
      })
      const cpItems = {}
      if (txt.length > 0) {
        cpItems['text/plain'] = new Blob([txt.join('\n')], { type: 'text/plain' })
      }
      cpItems['text/html'] = new Blob([htmlText], { type: 'text/html' })
      navigator.clipboard.write([new ClipboardItem(cpItems)])
    }
  }

  /**
   * @description being objects to front
   */
  bringObjectsToFront(objs: IWbObject[]) {
    const trgtObjs = objs.filter(obj => obj.selectable && !(obj as any).lock && obj.visible && !obj.localOnly)
    if (trgtObjs.length === 0) {
      return
    }
    const indexMap = new Map<string, number>()
    trgtObjs.forEach((obj: any) => {
      if (obj.id) {
        indexMap.set(obj.id, this.canvas.getObjects().indexOf(obj))
      }
      obj.bringToFront()
    })
    this.reorderObjects()
    this.addToHistory('reorder', trgtObjs, indexMap)
    this.canvas.requestRenderAll()
    this.eventBus['object-order'].emit({ target: trgtObjs })
  }

  /**
   * @description set order of objects
   */
  setObjectOrder(objs: fabric.Object[]) {
    this.eventBus['object-order'].emit({ target: objs })
  }

  /**
   * @description update board settings
   */
  updateSettings(val: Partial<Record<TemplateSettingType, Record<string, any>>>) {
    this.eventBus['update-settings'].emit({ val })
  }

  /**
   * @description send objects to back
   */
  sendObjectsToBack(objs: IWbObject[]) {
    const trgtObjs = objs.filter(obj => obj.selectable && !obj.lock && obj.visible && obj.id)
    if (trgtObjs.length === 0) {
      return
    }
    const indexMap = new Map<string, number>()
    trgtObjs.forEach((obj) => {
      if (obj.id) {
        indexMap.set(obj.id, this.canvas.getObjects().indexOf(obj))
      }
      obj.sendToBack()
    })
    this.reorderObjects()
    this.addToHistory('reorder', objs, indexMap)
    this.canvas.requestRenderAll()
    this.eventBus['object-order'].emit({ target: trgtObjs })
  }

  centerObject(obj: IWbObject) {
    this.canvas.discardActiveObject()
    const viewPadding = { x: 100, y: 100 } // <- Adjust this value to the desired empty space around the frame when zooming

    const availableWidth = (this.canvas.width || 0) - viewPadding.x * 2
    const availableHeight = (this.canvas.height || 0) - viewPadding.y * 2
    const frameWidth = (obj.width || 0) * (obj.scaleX || 0)
    const frameHeight = (obj.height || 0) * (obj.scaleY || 0)
    const widthZoomToFit = availableWidth / frameWidth
    const heightZoomToFit = availableHeight / frameHeight
    const zoomLevel = Math.min(widthZoomToFit, heightZoomToFit)

    const newLeft = viewPadding.x + (-(obj.left || 0) * zoomLevel) + (availableWidth - frameWidth * zoomLevel) / 2
    const newTop = viewPadding.y + (-(obj.top || 0) * zoomLevel) + (availableHeight - frameHeight * zoomLevel) / 2

    this.zoom(zoomLevel)
    this.canvas.setViewportTransform([zoomLevel, 0, 0, zoomLevel, newLeft, newTop])
    // if(setActive) {
    //   this.canvas.setActiveObject(obj)
    // }
    this.canvas.requestRenderAll()
  }

  moveToView(obj: IWbObject) {
    this.canvas.discardActiveObject()
    const viewPadding = { x: 10, y: 10 } // <- Adjust this value to the desired empty space around the frame when zooming

    const availableWidth = (this.canvas.width || 0) - viewPadding.x * 2
    const availableHeight = (this.canvas.height || 0) - viewPadding.y * 2
    const frameWidth = (obj.width || 0) * (obj.scaleX || 0)
    const frameHeight = (obj.height || 0) * (obj.scaleY || 0)
    const widthZoomToFit = availableWidth / frameWidth
    const heightZoomToFit = availableHeight / frameHeight
    const zoomLevel = Math.min(widthZoomToFit, heightZoomToFit, this.canvas.getZoom())
    const newLeft = viewPadding.x + (-(obj.left || 0) * zoomLevel) + (availableWidth - frameWidth * zoomLevel) / 2
    const newTop = viewPadding.y + (-(obj.top || 0) * zoomLevel) + (availableHeight - frameHeight * zoomLevel) / 2
    this.zoom(zoomLevel)
    this.canvas.setViewportTransform([zoomLevel, 0, 0, zoomLevel, newLeft, newTop])
    this.canvas.requestRenderAll()
    obj.setCoords()
    this.canvas.setActiveObject(obj)
  }

  addObjectsFromJson(objects: any[], isPaste = false, addToHistory = false) {
    return new Promise<fabric.Object[]>((resolve) => {
      fabric.util.enlivenObjects(objects, (newObjs) => {
        this.manageEnlivenConnectors(newObjs, isPaste)
        this.addObjects(newObjs, addToHistory)
        resolve(newObjs)
      }, '')
    })
  }

  addGroupFromJson(groupData: any) {
    return new Promise<fabric.Group>((resolve) => {
      fabric.util.enlivenObjects(groupData.objects, (newObjs) => {
        this.manageEnlivenConnectors(newObjs, false)

        const group = new WbGroup(newObjs, {
          ...groupData,
        })

        this.canvas.add(group)
        this.canvas.requestRenderAll()

        resolve(group)
      }, '')
    })
  }

  manageEnlivenConnectors(objects: Array<IWbObject>, isPaste: boolean) {
    const indexedObjects = objects.reduce((acu, cur) => (acu[cur.id!] = cur) && acu, {})
    let indexedObjectsByOldId = {}
    if (isPaste) {
      // on paste the newly create objects will have new id's, when copy objects their oldIds will be stored in oldId property
      indexedObjectsByOldId = objects.reduce((acu, cur) => (acu[(cur as any).oldId] = cur) && acu, {})
    }
    objects.forEach((object) => {
      if (object.type === whiteboardConstants.objectTypes.connector) {
        const connectorObject = object as WbConnector
        let startObject = indexedObjects[connectorObject.startObjectId]
        let endObject = indexedObjects[connectorObject.endObjectId!]
        if (isPaste) {
          // if paste update start and end object id to point to newly created objects
          startObject = indexedObjects[indexedObjectsByOldId[connectorObject.startObjectId].id]
          endObject = indexedObjects[indexedObjectsByOldId[connectorObject.endObjectId!].id]
          connectorObject.setProp('startObjectId', startObject.id)
          connectorObject.setProp('endObjectId', endObject.id)
        }
        Utility.addWbConnectorsConnectedObjectListeners(this.canvas, connectorObject, startObject, endObject)
      }
    })
  }

  performMoveToObject() {
    if (utils.isDefined(this.moveToObject)) {
      const frame = this.frames.value.find(x => x.id === this.moveToObject)
      if (frame) {
        this.centerObject(frame as IWbObject)
      }
    }
  }

  addToHistory(op: 'add' | 'rem' | 'mod' | 'reorder' | 'group' | 'ungroup' | 'assignImage', obj: IWbObject | IWbObject[], indexMap?: Map<string, number>, customData?: any) {
    const maxUndoHistory = 10

    if (this.historyPointer.value > 0) {
      for (let index = 0; index < this.historyPointer.value; index++) {
        this.history.value.pop()
      }
      this.historyPointer.value = 0
    }
    else {
      this.historyPointer.value = 0
    }
    if (this.history.value.length >= maxUndoHistory) {
      this.history.value.shift()
    }

    if (obj && !Array.isArray(obj) && obj.type === 'activeSelection') {
      // eslint-disable-next-line ts/ban-ts-comment
      // @ts-expect-error
      obj = obj.getObjects()
    }
    // const ao = this.canvas.getActiveObjects()
    // this.canvas.discardActiveObject()

    if (op === 'group') {
      const states: { org: string, cur: string }[] = []
      this.history.value.push({ op, obj, state: states })
    }
    else if (op === 'ungroup') {
      const states: { org: string, cur: string }[] = []
      this.history.value.push({ op, obj, state: states })
    }
    else if (Array.isArray(obj)) {
      const states: { org: string, cur: string }[] = []
      const simpleObjects: any[] = []

      obj.forEach((itm: IWbObject) => {
        const org = JSON.stringify(utils.getStateProperties(itm))
        itm.saveState()
        const cur = JSON.stringify(utils.getStateProperties(itm))
        if (op === 'mod') { states.push({ org, cur }) }
        simpleObjects.push(itm.toObject())
      })

      this.history.value.push({ op, obj: simpleObjects, state: op === 'mod' ? states : undefined, indexMap, customData })
    }
    else if (obj) {
      const org = JSON.stringify(utils.getStateProperties(obj))
      obj.saveState()
      const cur = JSON.stringify(utils.getStateProperties(obj))
      this.history.value.push({ op, obj: obj.toObject(), state: op === 'mod' ? { org, cur } : undefined, indexMap, customData })
    }

    this.history.value = [...this.history.value]

    // if (ao && ao.length > 0) {
    //   if (ao.length === 1) {
    //     this.canvas.setActiveObject(ao[0])
    //   }
    //   else {
    //     this.canvas.setActiveObject(new fabric.ActiveSelection(ao, { canvas: this.canvas }))
    //   }
    // }
  }

  async drawFromHistory(undo: boolean) {
    this.canvas.discardActiveObject()

    const histItem = this.history.value[this.history.value.length - this.historyPointer.value - 1]
    let newSelection: IWbObject | IWbObject[] | null = null

    const allObjs = this.canvas.getObjects() as Array<IWbObject>

    if (histItem.op === 'reorder') {
      if (Array.isArray(histItem.obj)) {
        histItem.obj.forEach((itm) => {
          const currentIndex = allObjs.findIndex(obj => obj.id === itm.id)
          if (currentIndex >= 0 && histItem.indexMap && allObjs[currentIndex]?.id && histItem.indexMap.has(allObjs[currentIndex].id!)) {
            const element = allObjs[currentIndex]
            this.canvas.remove(element)
            const insertIndex = Math.min(histItem.indexMap.get(element.id!)!, allObjs.length)
            this.canvas.insertAt(element, insertIndex, false)
            if (element.type === whiteboardConstants.objectTypes.frame && element instanceof WbFrame) {
              element.repositionTitle()
            }
            histItem.indexMap.set(element.id!, currentIndex)
          }
        })
      }
      else {
        const histItemObj = histItem.obj as IWbObject
        const currentIndex = allObjs.findIndex(obj => obj.id === histItemObj.id)
        if (currentIndex >= 0 && histItem.indexMap && allObjs[currentIndex]?.id && histItem.indexMap.has(allObjs[currentIndex].id!)) {
          const element = allObjs[currentIndex]
          this.canvas.remove(element)
          const insertIndex = Math.min(histItem.indexMap.get(element.id!)!, allObjs.length)
          this.canvas.insertAt(element, insertIndex, false)
          if (element.type === whiteboardConstants.objectTypes.frame && element instanceof WbFrame) {
            element.repositionTitle()
          }
          histItem.indexMap.set(element.id!, currentIndex)
        }
      }
    }
    else if (histItem.op === 'mod') {
      if (Array.isArray(histItem.obj)) {
        newSelection = []
        for (let index = 0; index < histItem.obj.length; index++) {
          const wbObj = allObjs.find(itm => itm.id === histItem.obj[index].id)
          if (wbObj && histItem.state && Array.isArray(histItem.state)) {
            const org = JSON.parse(histItem.state[index].org)
            const cur = JSON.parse(histItem.state[index].cur)
            wbObj.setOptions(undo ? org : cur)
            wbObj.dirty = true
            wbObj.setCoords()
            wbObj.saveState()
            if (org.lock !== cur.lock) {
              wbObj.setLock(undo ? org.lock : cur.lock)
            }
            if (wbObj.type === whiteboardConstants.objectTypes.frame && wbObj instanceof WbFrame) {
              wbObj.repositionTitle(wbObj.name)
            }
            if (wbObj.type === whiteboardConstants.objectTypes.dynamic) {
              (wbObj as WbDynamic).recalcObjectsPosition()
            }
            utils.addOrRemoveChildToFrames(this.frames.value, wbObj)
            this.canvas.fire('object:modified', { target: wbObj, ignoreHistory: true })
            newSelection.push(wbObj)
          }
          else {
            console.warn('Cannot find element with id', histItem.obj[index].id)
          }
        }
      }
      else {
        const histItemObj = histItem.obj as IWbObject
        const wbObj = allObjs.find(itm => itm.id === histItemObj.id)
        if (wbObj && histItem.state && !Array.isArray(histItem.state)) {
          const org = JSON.parse(histItem.state.org)
          const cur = JSON.parse(histItem.state.cur)
          wbObj.setOptions(undo ? org : cur)
          wbObj.dirty = true
          wbObj.setCoords()
          wbObj.saveState()
          utils.addOrRemoveChildToFrames(this.frames.value, wbObj)
          if (org.lock !== cur.lock) {
            wbObj.setLock(undo ? org.lock : cur.lock)
          }
          if (wbObj.type === whiteboardConstants.objectTypes.frame && wbObj instanceof WbFrame) {
            wbObj.repositionTitle(wbObj.name)
          }
          this.canvas.fire('object:modified', { target: wbObj, ignoreHistory: true })
          if (wbObj.type === whiteboardConstants.objectTypes.dynamic) {
            (wbObj as WbDynamic).recalcObjectsPosition()
          }
          newSelection = wbObj
        }
        else {
          console.warn('Cannot find element with id', histItemObj.id)
        }
      }
    }
    else if (histItem.op === 'group' || histItem.op === 'ungroup') {
      if ((histItem.op === 'group' && undo) || (histItem.op === 'ungroup' && !undo)) {
        // eslint-disable-next-line ts/ban-ts-comment
        // @ts-expect-error
        const element = allObjs.find(itm => itm.id === histItem.obj.id)
        if (element) {
          this.ungroupObject(element, false)
        }
        else {
          // eslint-disable-next-line ts/ban-ts-comment
          // @ts-expect-error
          console.warn('Cannot find group with id', histItem.obj.id)
        }
      }
      else {
        // eslint-disable-next-line ts/ban-ts-comment
        // @ts-expect-error
        const elements = histItem.groupsAllObjects
          .map(obj => allObjs.find(itm => itm.id === obj.id))
          .filter((obj): obj is IWbObject => obj !== undefined)

        this.groupObjects(elements, histItem.obj, false)
      }
    }
    else if ((histItem.op === 'add' && undo) || (histItem.op === 'rem' && !undo)) {
      const objectsToRemove: IWbObject[] = []
      if (Array.isArray(histItem.obj)) {
        histItem.obj.forEach((itm) => {
          const element = allObjs.find(obj => obj.id === itm.id)
          if (element) {
            objectsToRemove.push(element)
          }
          else {
            console.warn('Cannot find element with id', itm.id)
          }
        })
      }
      else {
        const histItemObj = histItem.obj as IWbObject
        const element = allObjs.find(obj => obj.id === histItemObj.id)
        if (element) {
          objectsToRemove.push(element)
        }
        else {
          console.warn('Cannot find element with id', histItemObj.id)
        }
      }
      this.removeObjects(objectsToRemove)
    }
    else if (histItem.op === 'assignImage' && histItem.customData?.undoHandler) {
      const objectsToModify = allObjs.filter(obj =>
        Array.isArray(histItem.obj)
          ? histItem.obj.some((itm: any) => itm.id === obj.id)
          : histItem.obj.id === obj.id,
      )

      if (undo) {
        await histItem.customData.undoHandler(objectsToModify[0])
      }
    }
    else {
      const objectsToAdd: IWbObject[] = []
      if (Array.isArray(histItem.obj)) {
        objectsToAdd.push(...histItem.obj)
      }
      else {
        objectsToAdd.push(histItem.obj)
      }
      fabric.util.enlivenObjects(objectsToAdd, (newObjs) => {
        this.addObjects(newObjs, false, histItem.indexMap)
      }, '')
    }

    this.canvas.requestRenderAll()
  }

  addObjects(newObjects: IWbObject[], addToHistory: boolean = false, indexMap?: Map<string, number>) {
    if (addToHistory) {
      this.addToHistory('add', newObjects.length > 1 ? newObjects : newObjects[0])
    }

    const canvasObjectsLen = this.canvas.getObjects().length

    newObjects.forEach((object) => {
      if (object.type === whiteboardConstants.objectTypes.dynamic) {
        this.canvas.insertAt(object, 0, false)
      }
      else if (object.id && indexMap && indexMap.has(object.id)) {
        const index = indexMap.get(object.id)!
        if (index >= canvasObjectsLen) {
          this.canvas.add(object)
        }
        else {
          this.canvas.insertAt(object, index, false)
        }
      }
      else {
        this.canvas.add(object)
      }
      if (object.type === whiteboardConstants.objectTypes.frame && object instanceof WbFrame) {
        object.repositionTitle()
      }
      object.saveState()
    })

    this.canvas.requestRenderAll()
  }

  removeObjects(objects: IWbObject[], addToHistory: boolean = false) {
    const fObjs = objects.filter(obj => !obj.preventDelete)
    if (fObjs.length <= 0) {
      return
    }
    const indexMap = new Map(fObjs.map(obj => [obj.id!, this.canvas.getObjects().indexOf(obj)]))
    if (addToHistory) {
      this.addToHistory('rem', fObjs.length > 1 ? fObjs : fObjs[0], indexMap)
    }

    fObjs.forEach((object) => {
      this.canvas.remove(object)
    })

    this.canvas.discardActiveObject().requestRenderAll()
  }

  undo() {
    if (this.historyPointer.value >= this.history.value.length) { return }
    // do not enable redo when image is deleted
    const histItem = this.history.value[this.history.value.length - this.historyPointer.value - 1]
    const isAssignImageOperation = histItem.op === 'assignImage'

    if (isAssignImageOperation) {
      this.drawFromHistory(true)
      this.history.value.splice(this.history.value.length - this.historyPointer.value - 1, 1)
      this.history.value = [...this.history.value]
    }
    else {
      this.drawFromHistory(true)
      this.historyPointer.value++
    }
  }

  redo() {
    if (this.historyPointer.value <= 0) { return }

    this.historyPointer.value--
    this.drawFromHistory(false)
  }

  unlinkObjects(objects: IWbObject[], addToHistory: boolean = false) {
    if (!objects || !isArray(objects) || objects.length <= 0) {
      return
    }

    objects.forEach((obj) => {
      if (obj.link && obj.link.id) {
        obj.link = {} as any
        this.canvas.fire('object:modified', { target: obj, ignoreHistory: !addToHistory })
      }
    })
  }

  groupObjects(objects: IWbObject[], groupSpecs?, addToHistory?) {
    if (!objects || !isArray(objects) || objects.length <= 0) {
      return
    }
    const fObjs = objects.filter(obj => !obj.preventDelete)
    if (fObjs.length <= 0) {
      return
    }

    this.canvas.discardActiveObject()

    const group = new WbGroup(null, groupSpecs)

    fObjs.forEach((obj) => {
      if (obj.type !== 'hotspot') {
        group.addWithUpdate(obj)
      }
    })

    group.saveState()
    this.canvas.add(group)
    this.removeObjects(fObjs, false)
    group.setCoords()

    if (addToHistory) {
      this.addToHistory('group', group as IWbObject)
    }

    this.canvas.setActiveObject(group)
    return group
  }

  ungroupObject(group, addToHistory = false) {
    if (!group || group.type !== 'group') {
      return
    }

    if (addToHistory) {
      this.addToHistory('ungroup', group)
    }

    let sel = group.toActiveSelection()
    const objects = sel.getObjects()

    // add the ungrouped objects to history
    const histItem = this.history.value[this.history.value.length - this.historyPointer.value - 1]
    histItem.groupsAllObjects = objects

    this.canvas.discardActiveObject()
    objects.forEach((object) => {
      object.saveState()
    })
    sel = new fabric.ActiveSelection(objects, { canvas: this.canvas })
    this.canvas.setActiveObject(sel)
    this.canvas.requestRenderAll()
    return sel
  }

  addDynamicGridObj(pos: IPoint) {
    this.eventBus['add-dynamic-grid'].emit({ pos })
  }

  onCanavasObjectsLoaded(wbDetails: Whiteboard) {
    this.eventBus['canvas-objects-loaded'].emit({ wbDetails })
  }

  handleDynamicGridChildrenProps() {
    // this.canvas.on('object:modified', (opt) => {
    //   if (opt.target && (opt.target as IWbObject).localOnly) {
    //     console.log('object:modified', opt.target)
    //   }
    // })
  }

  handleLinkingObjects() {
    this.canvas.on('mouse:down', () => {
      this._mouseDown = true
    })

    this.canvas.on('object:added', (e) => {
      if (e.target && e.target.type === whiteboardConstants.objectTypes.articleImage) {
        const ref = (e.target as any)?.dynamicGrid ? `dg|${(e.target as any).dynamicGrid.id}|${(e.target as any).articleId}` : `${(e.target as any).id}`
        const trgtObjs = this.canvas?._objects.filter((obj: any) => obj.link && obj.link.id === ref)
        if (trgtObjs && trgtObjs.length > 0) {
          trgtObjs.forEach((obj: any) => {
            console.log('bringing front', obj.id)
            obj.bringToFront()
          })
          this.reorderObjects()
        }
      }
      // when the discussion is added on top an article image it should be linked to the article
      if (e.target && e.target.type === whiteboardConstants.objectTypes.discussion) {
        const discussionIcon = e.target as IWbObject

        // all other objects excluding the discussion itself, as we need to attach comments to any object
        // not allowing adding comment for frame as frame can contain other objects
        const otherObjects = this.canvas.getObjects().filter(obj =>
          obj !== discussionIcon && obj.type !== whiteboardConstants.objectTypes.frame && obj.type !== whiteboardConstants.objectTypes.dynamic,
        )

        for (const containerObj of otherObjects) {
          if (utils.isContainedWithinObject(discussionIcon.getBoundingRect(true), containerObj)) {
            this.handleDiscussionIconAdded(discussionIcon, containerObj)
            break
          }
        }
      }
    })

    // Highlight the object that can be linked to when moving the mouse over it
    this.canvas.on('object:moving', (e) => {
      const activeObjs = this.canvas.getActiveObjects() as IWbObject[]

      if (activeObjs.length > whiteboardConstants.maxObjectsForLinking) { return }

      if (this._mouseDown) {
        const linkableObjs = this.getLinkableObjects()
        if (linkableObjs.length > 0) {
          const objs = this.canvas.getObjects() as Array<IWbObject>
          // Find the last object in the objs array where the e.pointer is inside the object
          const foundObj = [...objs].reverse().find((obj) => {
            if (e.pointer && obj.canBeLinkedTo && !activeObjs.some(o => o.id === obj.id)) {
              const isInside = obj.containsPoint(e.pointer, null, true)
              return isInside
            }
            return false
          })
          if (foundObj && foundObj.canBeLinkedTo) {
            if (foundObj.id !== this._linkableObject?.id && foundObj.type !== whiteboardConstants.objectTypes.frame && foundObj.type !== whiteboardConstants.objectTypes.dynamic) {
              this.highlightLinkableObject(this._linkableObject, false)
              this.highlightLinkableObject(foundObj, true)
              this._linkableObject = foundObj
            }
          }
          else {
            if (this._linkableObject) {
              this.highlightLinkableObject(this._linkableObject, false)
              this._linkableObject = undefined
            }
          }
        }
      }

      // Move all linked objects with the selected object
      const activeObjsIds = new Set(activeObjs.filter(x => x.id).map(obj => obj.id!))
      const objsCanBeLinkedTo = activeObjs.filter(obj => obj.canBeLinkedTo)
      if (objsCanBeLinkedTo && objsCanBeLinkedTo.length > 0) {
        const movingObjs = arrayToStringDictionary(objsCanBeLinkedTo, 'id')
        const objs = this.canvas.getObjects() as Array<IWbObject>
        const linkedObjs = objs.filter(obj => (obj.link && obj.id && movingObjs.hasOwnProperty(obj.link.id) && !activeObjsIds.has(obj.id)) || (obj.data && obj.data.link && movingObjs.hasOwnProperty(obj.data.link.id) && !activeObjsIds.has(obj.data.id)))

        linkedObjs.forEach((obj) => {
          const link = obj.link || obj.data?.link
          const objId = link.id
          const tm = movingObjs[objId].calcTransformMatrix()
          const pos = fabric.util.qrDecompose(tm)
          obj.setPositionByOrigin(new fabric.Point(pos.translateX + link!.x, pos.translateY + link!.y), 'center', 'center')
          obj.setCoords()
        })
        this.canvas.requestRenderAll()
      }
    })

    // When mouse is released, link the objects together
    this.canvas.on('mouse:up', () => {
      this._mouseDown = false
      if (this._linkableObject && this._linkableObject.id) {
        let linkToId: string | null = this._linkableObject.id
        if ((this._linkableObject as any).dynamicGrid) {
          linkToId = ((this._linkableObject as any).dynamicGrid as WbDynamic).getChildObjectReference(linkToId)
          if (!linkToId) {
            console.warn('Unable to resolve dynamic grid object', this._linkableObject.id)
            return
          }
        }
        const objsToLink = this.getLinkableObjects()
        this.canvas.discardActiveObject()
        const linkableObjCoords = fabric.util.qrDecompose(this._linkableObject.calcTransformMatrix())
        if (objsToLink.length > 0) {
          objsToLink.forEach((obj) => {
            const coord = fabric.util.qrDecompose(obj.calcTransformMatrix())
            obj.link = { id: linkToId || '', x: coord.translateX - linkableObjCoords.translateX, y: coord.translateY - linkableObjCoords.translateY }
            obj.bringToFront()
            this.canvas.fire('object:modified', { target: obj, ignoreHistory: false })
            console.log('linking', obj, linkToId)
          })
          this.reorderObjects()
        }

        this.highlightLinkableObject(this._linkableObject, false)
        this._linkableObject = undefined
      }
    })

    this.canvas.on('object:modified', (e) => {
      if (!e.target) { return }
      const allObjs = (this.canvas.getObjects() as Array<IWbObject>).filter(obj => obj.id)
      const allObjsDict = arrayToStringDictionary(allObjs, 'id')
      const targetObjs = e.target.isType('activeSelection') ? (e.target as fabric.ActiveSelection)._objects : [e.target]

      // Update the coordinates of linked objects in relation to the object they are linked to, when they are moved
      const linkedObjs = targetObjs.filter((obj: any) => (obj.link && obj.link.id) || (obj.data && obj.data.link && obj.data.link.id))
      linkedObjs.forEach((obj: any) => {
        let linkedObj
        const link = obj.link || obj.data?.link
        if (link && link.id.startsWith('dg|')) {
          // This object is linked to a child object of a dynamic grid
          const dg = allObjsDict[link.id.split('|')[1]] as WbDynamic
          if (dg) {
            linkedObj = dg.getChildObjectByReference(link.id)
          }
        }
        else {
          linkedObj = allObjsDict[link.id]
        }

        if (!linkedObj) { return }
        const coord = fabric.util.qrDecompose(obj.calcTransformMatrix())
        const linkedCoords = fabric.util.qrDecompose(linkedObj.calcTransformMatrix())
        if (link.x !== coord.translateX - linkedCoords.translateX || link.y !== coord.translateY - linkedCoords.translateY) {
          if (obj.link) {
            console.warn('Updating link coordinates for object', obj, 'from', obj.link, 'to', { id: obj.link.id, x: coord.translateX - linkedCoords.translateX, y: coord.translateY - linkedCoords.translateY })
            obj.link = { id: obj.link.id, x: coord.translateX - linkedCoords.translateX, y: coord.translateY - linkedCoords.translateY }
          }
          else if (obj.data && obj.data.link) {
            console.warn('Updating data link coordinates for object', obj, 'from', obj.data.link, 'to', { id: obj.data.link.id, x: coord.translateX - linkedCoords.translateX, y: coord.translateY - linkedCoords.translateY })
            obj.data.link = { id: obj.data.link.id, x: coord.translateX - linkedCoords.translateX, y: coord.translateY - linkedCoords.translateY }
          }
          this.canvas.fire('object:modified', { target: obj, ignoreHistory: true })
        }
      })

      // Fire the object:modified event for all linked objects when the object they are linked to is modified
      const linkableObjs = targetObjs.filter((o: any) => !!o.canBeLinkedTo)
      if (linkableObjs.length > 0) {
        const targetObjs = allObjs.filter((obj: any) => linkableObjs.some((obj2: any) => (obj.link?.id && obj2.id?.includes(obj.link?.id)) || (obj.data && obj.data.link?.id && obj2.id?.includes(obj.data.link?.id))))
        targetObjs.forEach((obj: any) => {
          this.canvas.fire('object:modified', { target: obj, ignoreHistory: true })
        })
      }
    })
  }

  handleDiscussionIconAdded(discussionIcon: IWbObject, container: any) {
    if (utils.isContainedWithinObject(discussionIcon.getBoundingRect(true), container)) {
      const linkToId = (container as any)?.dynamicGrid
        ? `dg|${(container as any).dynamicGrid.id}|${(container as any).articleId}`
        : container.id
      const coord = fabric.util.qrDecompose(discussionIcon.calcTransformMatrix())
      const linkedCoords = fabric.util.qrDecompose(container.calcTransformMatrix())

      discussionIcon.link = {
        id: linkToId,
        x: coord.translateX - linkedCoords.translateX,
        y: coord.translateY - linkedCoords.translateY,
      }

      discussionIcon.bringToFront()
      this.reorderObjects()
      console.log('Linked discussion to article image!')
    }
  }

  getLinkableObjects() {
    const activeObj = this.canvas.getActiveObject() as any
    let activeObjs = [] as Array<IWbObject>
    if (activeObj && activeObj._objects && activeObj._objects.length > 1) {
      activeObjs = activeObj._objects as Array<IWbObject>
    }
    else {
      activeObjs = [activeObj as IWbObject]
    }
    return activeObjs.filter(obj => obj.canLink && obj.type !== whiteboardConstants.objectTypes.designOption)
  }

  highlightLinkableObject(obj: IWbObject | undefined, highlight: boolean) {
    if (obj) {
      if (highlight) {
        obj.set({ stroke: 'red', strokeWidth: 2 })
      }
      else {
        obj.set({ stroke: '', strokeWidth: 0 })
      }
      this.canvas.requestRenderAll()
    }
  }

  selectNextObject(previous: boolean) {
    const selectedObjects = this.canvas.getActiveObjects()
    let nextObject

    if (selectedObjects.length > 0) {
      const currentObject = selectedObjects.length === 1 ? selectedObjects[0] : previous ? this.getAdjacentObject(selectedObjects, null, 'previous') : this.getAdjacentObject(selectedObjects, null, 'next')
      nextObject = this.getAdjacentObject(this.canvas.getObjects(), currentObject, previous ? 'previous' : 'next')
    }
    else {
      nextObject = this.getAdjacentObject(this.canvas.getObjects(), null, previous ? 'previous' : 'next')
    }
    if (nextObject) {
      this.canvas.setActiveObject(nextObject.o)
      this.moveToView(nextObject.o)
    }
  }

  getAdjacentObject(objects: any[], currentObject: any = null, direction: 'next' | 'previous' = 'next') {
    // Sort objects from top-left to bottom-right
    const sorted = objects.reduce((acu, cur: any) => {
      if (cur.visible && cur.selectable && cur.id) {
        acu.push({ o: cur, p: cur.getCenterPoint() })
      }
      return acu
    }, [] as { o: fabric.Object, p: fabric.Point }[])
      .sort((a, b) => {
        if (a.p.y === b.p.y) { return a.p.x - b.p.x }
        return a.p.y - b.p.y
      })

    if (!currentObject) {
      if (direction === 'next') { return sorted[0] }
      return sorted[sorted.length - 1]
    }

    // Find the index of the currentObject
    const index = sorted.findIndex((obj: any) =>
      obj.o.id === currentObject.id,
    )

    if (index === -1) {
      return sorted[0] // Fallback to first if not found
    }

    if (direction === 'next') {
      return sorted[(index + 1) % sorted.length] // Wrap around forward
    }
    else if (direction === 'previous') {
      return sorted[(index - 1 + sorted.length) % sorted.length] // Wrap around backward
    }

    return null // Invalid direction
  }

  swapArticle(swapArticleId, targetArticle) {
    const addObjects: IWbObject[] = []
    const removeObjects: IWbObject[] = []
    const canvasObjects = this.canvas.getObjects() as Array<IWbObject>
    canvasObjects?.forEach(async (obj: any) => {
      if (obj.articleId && obj.articleId === swapArticleId && (obj.type === whiteboardConstants.objectTypes.articleDetails || (obj.type === whiteboardConstants.objectTypes.articleImage && obj instanceof WbArticleImage && !obj.dynamicGrid))) {
        removeObjects.push(obj)
        const swap = obj.toObject()
        swap.articleId = targetArticle.Id
        addObjects.push(swap)
      }
      else if (obj.type === whiteboardConstants.objectTypes.group) {
        const groupObject = obj as WbGroup
        const objectsToSwap: IWbObject[] = groupObject._objects.filter((object: any) => object.articleId && object.articleId === swapArticleId && (object.type === whiteboardConstants.objectTypes.articleDetails || (object.type === whiteboardConstants.objectTypes.articleImage && !obj.dynamicGrid))) as IWbObject[]
        if (objectsToSwap.length) {
          removeObjects.push(obj)
          const swap = groupObject.toObject()
          swap._objects.forEach((object) => {
            const foundObject: any = objectsToSwap.find(old => old.id === object.id)
            if (foundObject) {
              foundObject.articleId = targetArticle.Id
            }
          })
          addObjects.push(swap)
        }
      }
    })
    if (removeObjects.length) {
      this.removeObjects(removeObjects)
      fabric.util.enlivenObjects(addObjects, (newObjs) => {
        this.addObjects(newObjs)
      }, '')
    }
  }
}

export interface ICanvasZoomEvent {
  event: 'zoom'
  point?: IPoint
  factor: number
}

export interface ICanvasPanEvent {
  event: 'pan'
  viewport: number[]
}

interface IObjectAddedEvent {
  target: fabric.Object
}

interface IObjectRemovedEvent {
  target: fabric.Object
}

interface IObjectModifiedEvent {
  target: fabric.Object
}

interface IObjectOrderEvent {
  target: fabric.Object[]
}

interface IUpdateSettingsEvent {
  val: Partial<Record<TemplateSettingType, Record<string, any>>>
}

interface IAddDynamicGridEvent {
  pos: IPoint
}

export interface ICanvasObjectsLoadedEvent {
  wbDetails: Whiteboard
}

type CanvasEventName = 'zoom' | 'pan' | 'object-added' | 'object-removed' | 'object-modified' | 'object-moving' | 'object-modified-server' | 'object-order' | 'update-settings' | 'add-dynamic-grid' | 'canvas-objects-loaded'

type CanvasEventType<T> =
  T extends 'zoom' ? ICanvasZoomEvent :
    T extends 'pan' ? ICanvasPanEvent :
      T extends 'object-added' ? IObjectAddedEvent :
        T extends 'object-removed' ? IObjectRemovedEvent :
          T extends 'object-modified' ? IObjectModifiedEvent :
            T extends 'object-moving' ? IObjectModifiedEvent :
              T extends 'object-modified-server' ? IObjectModifiedEvent :
                T extends 'object-order' ? IObjectOrderEvent :
                  T extends 'update-settings' ? IUpdateSettingsEvent :
                    T extends 'add-dynamic-grid' ? IAddDynamicGridEvent :
                      T extends 'canvas-objects-loaded' ? ICanvasObjectsLoadedEvent :
                        never

export default Whiteboard
